import React, { useRef, useState } from 'react';
import { errorLines, reconcileRespondents } from '../../api';
import type { ReconcileResult, Respondent } from '../../types';
import { Button, InlineAlert, Modal, SelectField, TextField, useToast } from '../../ui';
import { FINAL_STATUSES, describeCounts, statusLabel } from './respondentStatus';

interface ReconcileDialogProps {
  open: boolean;
  projectId: string;
  /** the respondents that would change */
  respondents: Respondent[];
  onClose: () => void;
  /** called after the server answered, so the list can reload (also when only some items were changed) */
  onDone: (result: ReconcileResult) => void;
}

/**
 * Change the final status of the chosen respondents (POST /project/{id}/respondents/reconciliations).
 * It takes effect immediately on the server, and statuses feed the statistics and later invoicing, so the dialog says what is
 * about to change and waits for a click before sending. Nothing is lost: each change is kept in the respondent's history
 * (source "reconciliation", with the old status) and a respondent can be reconciled again to put it back.
 * The server may refuse some items and still change the others; the refused ones are listed here.
 */
export function ReconcileDialog({ open, projectId, respondents, onClose, onDone }: ReconcileDialogProps) {
  const { notify } = useToast();
  const [status, setStatus] = useState<string>('qualityTerminate');
  const [reason, setReason] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setStatus('qualityTerminate'); setReason(''); setProblems([]); setFailed([]); }
  }

  const n = respondents.length;
  const summary = `Change ${n} ${n === 1 ? 'respondent' : 'respondents'} to ${statusLabel(status)} (${describeCounts(respondents.map((r) => r.status))})?`;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current || n === 0) return;
    savingRef.current = true;
    setSaving(true);
    setProblems([]);
    setFailed([]);
    try {
      const why = reason.trim();
      const result: ReconcileResult = await reconcileRespondents(projectId, respondents.map((r) => ({ id: r.id, status, ...(why ? { reason: why } : {}) })));
      onDone(result);
      if (result.failed > 0) {
        // the others were changed: say so, and list what the server refused
        notify(`${result.updated} updated, ${result.failed} not changed`, { tone: 'info', duration: 7000 });
        setFailed(result.results.filter((x) => x.ok === false).map((x) => `${(x.id || '').slice(0, 8)}: ${x.error || x.message || 'not changed'}`));
      } else {
        notify(`${result.updated} ${result.updated === 1 ? 'respondent' : 'respondents'} changed to ${statusLabel(status)}`, { tone: 'ok' });
        onClose();
      }
    } catch (err) {
      setProblems(errorLines(err));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const done = failed.length > 0;

  return (
    <Modal
      open={open}
      size="md"
      title="Change respondent status"
      onClose={() => { if (!savingRef.current) onClose(); }}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{done ? 'Close' : 'Cancel'}</Button>
          {!done && <Button type="submit" form="reconcile-form" loading={saving} disabled={n === 0}>Change status</Button>}
        </>
      )}
    >
      <form id="reconcile-form" onSubmit={submit} noValidate className="space-y-4">
        {!done && <p className="text-sm font-semibold text-ink" role="status">{summary}</p>}
        <SelectField label="New status" value={status} onChange={(e) => setStatus(e.target.value)} disabled={done}>
          {FINAL_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </SelectField>
        <TextField label="Reason (optional)" value={reason} maxLength={240} onChange={(e) => setReason(e.target.value)} disabled={done} hint="For example: speeder. Kept in each respondent's history." />
        <p className="text-xs text-muted">This takes effect immediately and is used in the statistics. Every change is kept in the respondent's history, so it can be changed back.</p>
        {failed.length > 0 && (
          <InlineAlert tone="warn">
            <b>Some respondents were not changed:</b>
            <ul className="ml-4 mt-1 list-disc">{failed.map((m) => <li key={m}>{m}</li>)}</ul>
          </InlineAlert>
        )}
        {problems.length > 0 && (
          <InlineAlert>{problems.length === 1 ? problems[0] : <><b>{problems[0]}</b><ul className="ml-4 mt-1 list-disc">{problems.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}</InlineAlert>
        )}
      </form>
    </Modal>
  );
}
